import { OptionType } from '@insurancemenu/imenu-components-lib/dist/types';
import { FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { getEffectiveDatesOptions } from 'global/services-helpers/domainService';
import { makeAutoObservable, reaction } from 'mobx';
import { DomainProductLinesDelegate } from 'store/modules/initiate-quote/domainProductLinesDelegate';
import { getRootStoreInstance, RootStore } from 'store/rootStore';

type AltusQuoteProposalInfoFields = {
  proposalName: FormControl<string>;
  effectiveDate: FormControl<string>;
  zip: FormControl<string>;
  state: FormControl<string>;
  county: FormControl<string>;
};

export class AltusQuoteProposalInfoFormDelegate {
  private readonly _rootStore: RootStore = getRootStoreInstance();
  public readonly domainProductLinesDelegate: DomainProductLinesDelegate;
  public proposalForm: FormController<AltusQuoteProposalInfoFields>;

  public get effectiveDatesOptions(): OptionType[] {
    return getEffectiveDatesOptions(this._rootStore.domainStoreV2.effectiveDates);
  }

  public get isValid(): boolean {
    return (
      this.proposalForm.basicFieldsValidationsPassed &&
      !this.proposalForm.isLoading &&
      !this.domainProductLinesDelegate.isLoading &&
      // Covered lines are not locked on this page, so this is domain-available GROUP lines only.
      this.domainProductLinesDelegate.hasQuotableProductLine
    );
  }

  private get _canFetchProductLines(): boolean {
    const { effectiveDate, zip, state, county } = this.proposalForm.fields;

    return !!(
      !effectiveDate.unpassedValidationMsg &&
      !zip.unpassedValidationMsg &&
      !state.unpassedValidationMsg &&
      !county.unpassedValidationMsg
    );
  }

  constructor(domainProductLinesDelegate: DomainProductLinesDelegate) {
    this.domainProductLinesDelegate = domainProductLinesDelegate;
    this.proposalForm = FormController.create(
      {
        proposalName: new FormControl<string>('', {
          validations: ['printableCharacters'],
        }),
        effectiveDate: new FormControl<string>(''),
      },
      {
        capabilities: [
          {
            type: 'zip',
            params: {
              state: {
                customValidations: [
                  {
                    check: this._rootStore.domainStoreV2.isEligibleState,
                    msg: 'State is ineligible',
                  },
                ],
              },
            },
          },
        ],
      },
    );

    reaction(
      () => [
        this.proposalForm.fields.effectiveDate.value,
        this.proposalForm.fields.zip.value,
        this.proposalForm.fields.state.value,
        this.proposalForm.fields.county.value,
      ],
      async () => {
        if (!this._canFetchProductLines) {
          this.domainProductLinesDelegate.clearProductLines();
          return;
        }

        await this.domainProductLinesDelegate.getDomainProductLines(this.proposalForm.getFormDataObject(), true);
      },
    );

    makeAutoObservable(this, { domainProductLinesDelegate: false }, { autoBind: true });
  }
}
